import { useState, type FormEvent } from 'react';
import { changePassword } from '../api/client';
import { IconEye, IconEyeOff } from '../components/icons';
import { ErrorBanner } from '../components/ui';

const MIN_PASSWORD_LENGTH = 10; // matches the server rule

export function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(new Error(`The new password must be at least ${MIN_PASSWORD_LENGTH} characters.`));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(new Error('The new passwords do not match.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(currentPassword, newPassword);
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 400, marginTop: '-10vh' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 24 }}>Change password</h1>
          <p className="sub" style={{ marginTop: 8 }}>Update your account password</p>
        </div>

        <ErrorBanner error={error} />

        {done && (
          <div className="banner banner-success" role="status" style={{ marginBottom: 24 }}>
            <div>
              Your password has been changed. You stay signed in here; any other device you were
              signed in on has been signed out.
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            <form onSubmit={submit}>
              <div className="field">
                <label htmlFor="cp-current">Current password</label>
                <input
                  id="cp-current"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  autoFocus
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="cp-new">New password</label>
                <input
                  id="cp-new"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <div className="field-hint">At least {MIN_PASSWORD_LENGTH} characters.</div>
              </div>
              <div className="field">
                <label htmlFor="cp-confirm">Confirm new password</label>
                <input
                  id="cp-confirm"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
                >
                  {showPassword ? <IconEyeOff /> : <IconEye />}
                  {showPassword ? 'Hide passwords' : 'Show passwords'}
                </button>
              </div>
              <div style={{ marginTop: 18 }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  disabled={busy || !currentPassword || !newPassword}
                >
                  {busy ? <span className="spinner" /> : 'Change password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
